'use-client'

import React, { useContext, useRef } from "react"
import { useRouter } from "next/navigation";
// my imports:
import Streak from "@/app/components/Streak"
import UI from "@/app/lib/ui/ui"
import { openFeedbackWindow } from "@/app/lib/system/openFeedbackWindow"
import { fetchPyramidPuzzle } from "@/app/lib/system/fetchPuzzle"
import { Contexts } from "@/app/components/Context"
import { GameManager, PyramidGameObj } from "@/app/lib/game-state/game-manager"
import { AnyGameObj, GameMode } from "@/app/lib/game-state/game-types"
import { InternalRouter } from "@/app/lib/routing/internal-router"
import { HoverElement, useHoverMonitor } from "@/app/lib/system/use-hover-monitor"
import { revealAnimation } from "@/app/lib/ui/reveal-animation"
import { Path } from "@/app/lib/routing/path";
// styles:
import styles from "@/app/styles/menu.module.css"
import { PyramidGameState } from "@/app/lib/game-state/pyramid-game-state"
import { FullStory, LevelName } from "@/app/lib/levels/full-story";

export default function Menu() {

    const gameManager: GameManager | null = useContext(Contexts.GameManager);
    const activeGame: AnyGameObj | null = useContext(Contexts.ActiveGame);
    const ui: UI | null = useContext(Contexts.UI);

    if (!gameManager || !activeGame || !ui ) {
        console.warn('bad context in Menu');
        return;
    }

    const iRouter = new InternalRouter(gameManager, ui);
    
    // for the level select:
    const currentLevelName: LevelName | undefined = gameManager.levels.getCurrentData()?.levelName;

    // THE LAYOUT FOR THE STORY MENU:
    if (gameManager.activeMode === 'story') {

        const currentLevel: number = gameManager.levels.current;
        const maxLevel: number = gameManager.levels.max;

        return(
            <div className="menuArea">

                <div>
                    <button className={`${styles.menuButton} ${styles.unicodeButton}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            iRouter.goPath('/story/levels');
                        }}
                    >
                        <span className={styles.unicodeMenu}>
                            ☰
                        </span>
                    </button>
                    <button className={
                        gameManager.levels.current === 1
                            ? `${styles.menuButton} ${styles.inactiveButton} ${styles.unicodeButton}`
                            : `${styles.menuButton} ${styles.unicodeButton}`
                        }
                        onClick={(e) => {
                            e.stopPropagation();
                            if (currentLevel > 1) {
                                iRouter.goBackLevel();
                            }
                        }}>
                        <span className={styles.unicodeArrow}>
                            ←
                        </span>
                    </button>
                    <button className={
                        gameManager.levels.current === maxLevel
                            ? `${styles.menuButton} ${styles.inactiveButton} ${styles.unicodeButton}`
                            : `${styles.menuButton} ${styles.unicodeButton}`
                        }
                    onClick={(e) => {
                            e.stopPropagation();
                            if (currentLevel < maxLevel) {
                                iRouter.goForwardLevel();
                            }
                        }}>
                    <span className={styles.unicodeArrow}>
                        →
                    </span>
                    </button>
                    &nbsp;{currentLevel}/{maxLevel}
                </div>

                
                <div>
                    <button className={styles.menuButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            gameManager.levels.resetCurrent();
                            ui.requestAutoFocus();
                            ui.requestGameBoxAnimation();
                        }}
                    >
                        reset level
                    </button>
                    <a href = '/builder'>
                        <button className={styles.menuButton}>
                            build your own
                        </button>
                    </a>
                </div>

                <div>
                    <button className={styles.menuButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            iRouter.goPath('/story/tutorial')
                        }}
                    >
                        how to play
                    </button>
                    <button className={styles.menuButton}
                        onClick={() => openFeedbackWindow(gameManager)}
                    >
                        feedback
                    </button>
                </div>

                <div>
   
                </div>
                <ModeButton 
                    currentMode='story'
                    iRouter={iRouter}
                />

                <br />
                <div className={styles.footerText}>
                    Story mode is experimental. Your progress may be lost.
                </div>

            </div>
        )
    }

    // THE LAYOUT FOR THE PYRAMID MENU
    return (
        <div className="menuArea">
            <div>
                <button className={styles.menuButton}
                    onClick={async () => {
                        // streak resets only trigger if puzzle was not completed
                        if (!activeGame.state.completed) {
                            // streak resets:
                            if (ui.userHistory.streakCount > 0) {
                                ui.resetStreak('sad');
                            } else {
                                ui.resetStreak(null);
                            }
                        }
                        // no matter what, new puzzle
                        const fetched = await fetchPyramidPuzzle(
                            gameManager,
                            ui
                        );
                        if (fetched) {
                            ui.setLoaded('pyramid', true);
                        }
                    }}
                >
                    new puzzle
                </button>
                <button className={styles.menuButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (activeGame.state.completed)
                            return;
                        ui.defocusAll();
                        if (activeGame.state instanceof PyramidGameState) {
                            revealAnimation({
                                pyramid: activeGame as unknown as PyramidGameObj, // I KNOW THIS IS KOSHER
                                ui: ui,
                                trueVictory: false,
                                intervalDelay: .02,
                                animations: ui.wordBoxAnimations.accept
                            })
                        }
                        else {
                            activeGame.update(state => {
                                state.reveal(false)
                            });
                        }
                    }}    
                >
                    reveal
                </button>
            </div>
            
            <div>
                <button className={styles.menuButton}
                    onClick={async () => {
                        // if puzzle is finished, fetch a new one for tutorial
                        if (activeGame.state.completed) {
                            const fetched = await fetchPyramidPuzzle(
                                gameManager,
                                ui
                            );
                            if (fetched) {
                                ui.setLoaded('pyramid', true);
                            }
                        }
                        // start the tutorial
                        iRouter.goPath('/tutorial');
                    }}
                >
                    how to play
                </button>          
                <button className={styles.menuButton}
                    onClick={() => openFeedbackWindow(gameManager)}
                >
                    feedback
                </button>
            </div>

            {ui.debugMode &&
                <ModeButton 
                    currentMode={'pyramid'}
                    iRouter={iRouter}
                />
            }
        
            <Streak />
            
            <br />
            <div className={styles.footerText}>
                Alphabet Soup is in beta. Please help improve it by <a href=''
                    onClick={(e) => {
                        e.preventDefault();
                        openFeedbackWindow(gameManager);
                    }}
                >
                    submitting feedback
                </a>.
            </div>
        </div>
    )
}

type LevelSelectProps = {
    currentLevelName: LevelName | undefined,
    fullStory: FullStory,
    iRouter: InternalRouter
}

export function LevelSelect({ currentLevelName, fullStory, iRouter} : LevelSelectProps) {

    if (!currentLevelName)
        return null;

    return (
        <div>
            <select
                name="level"
                id="level"
                onChange={(e) => {
                    iRouter.goLevelName(e.target.value)
                }}
            >
                {fullStory.levelDatas.map(ld => 
                    <option
                        value={ld.levelName}
                        key={ld.levelName}
                    >
                        {ld.levelName}
                    </option>
                )
                }
            </select>
        </div>
    )
}

type ModeButtonProps = {
    currentMode: GameMode | null
    iRouter: InternalRouter | null
}
export function ModeButton({ currentMode, iRouter }: ModeButtonProps) {
    const originalRef = useRef<HTMLButtonElement | null>(null);
    const storyRef = useRef<HTMLButtonElement | null>(null);
    const router = useRouter();

    const hoverElements: HoverElement<GameMode | null>[] = [
        {
            ref: originalRef,
            value: 'pyramid'
        },
        {
            ref: storyRef,
            value: 'story'
        }
    ]

    const hover = useHoverMonitor(hoverElements);
    let highlightedMode: GameMode | null = currentMode;
    if (
        hover.unique // it's not null
        && hover.unique !== currentMode // but it's not currentMode
    ) {
        highlightedMode = hover.unique;
    }

    function smartRoute(path: Path) {
        if (iRouter) {
            iRouter.goPath(path);
        } else {
            router.push(path);
        }
    }

    return (
        <div>
            <span className={styles.modeButtons}>
                <button 
                    className={`
                        ${styles.menuButton}
                        ${styles.modeButton}
                        ${styles.originalModeButton}
                        ${currentMode === 'pyramid'
                            && styles.currentModeButton
                        }
                        ${highlightedMode === 'pyramid'
                            && styles.modeButtonHighlight}
                    `}
                    ref={originalRef}

                    onClick={(e) => {
                        e.stopPropagation();
                        if (currentMode === 'pyramid') {
                            return;
                        }
                        smartRoute('/');
                    }}
                >
                    original mode
                </button>
                <button
                    className={`
                        ${styles.menuButton}
                        ${styles.modeButton}
                        ${styles.storyModeButton}
                        ${currentMode === 'pyramid'
                            && styles.currentModeButton
                        }
                        ${highlightedMode === 'story'
                            && styles.modeButtonHighlight}
                    `}
                    ref={storyRef}

                    onClick={(e) => {
                        e.stopPropagation();
                        if (currentMode === 'story') {
                            return;
                        }
                        smartRoute('/story');
                    }}
                >
                    story mode
                </button>
            </span>
        </div>
    )
}